import { useState, useCallback } from 'react';
import type { CertificationType, CourseLayout } from '../../../types/course';
import type { WizardStepNumber, WizardState } from './wizardTypes';

export interface WizardInitialData {
  courseId: string;
  title?: string;
  description?: string | null;
  image_url?: string | null;
  estimated_duration_minutes?: number | null;
  layout?: CourseLayout;
  certification_type?: CertificationType | null;
  certificate_file_key?: string | null;
}

function buildInitialState(init?: WizardInitialData): WizardState {
  if (!init) {
    return {
      courseId: null,
      steps: {
        1: { status: 'active' },
        2: { status: 'pending' },
        3: { status: 'pending' },
        4: { status: 'pending' },
      },
    };
  }

  // Edit mode — course already exists
  return {
    courseId: init.courseId,
    steps: {
      1: { status: 'saved' },
      2: { status: 'saved' },
      3: { status: 'active' },
      4: { status: 'active' },
    },
  };
}

export function useWizardState(initialData?: WizardInitialData) {
  const [state, setState] = useState<WizardState>(() => buildInitialState(initialData));

  const setCourseId = useCallback((id: string) => {
    setState((prev) => ({
      ...prev,
      courseId: id,
      steps: {
        ...prev.steps,
        // Unlock steps 2-4 once courseId exists
        2: { status: prev.steps[2].status === 'pending' ? 'active' : prev.steps[2].status },
        3: { status: prev.steps[3].status === 'pending' ? 'active' : prev.steps[3].status },
        4: { status: prev.steps[4].status === 'pending' ? 'active' : prev.steps[4].status },
      },
    }));
  }, []);

  const markStepSaved = useCallback((step: WizardStepNumber) => {
    setState((prev) => ({
      ...prev,
      steps: {
        ...prev.steps,
        [step]: { status: 'saved' },
      },
    }));
  }, []);

  const markStepActive = useCallback((step: WizardStepNumber) => {
    setState((prev) => ({
      ...prev,
      steps: {
        ...prev.steps,
        [step]: { status: 'active' },
      },
    }));
  }, []);

  const isStepEnabled = useCallback(
    (step: WizardStepNumber): boolean => {
      if (step === 1) return true;
      return state.courseId !== null;
    },
    [state.courseId]
  );

  const savedStepCount = Object.values(state.steps).filter((s) => s.status === 'saved').length;
  const allStepsSaved = savedStepCount === 4;

  return {
    state,
    setCourseId,
    markStepSaved,
    markStepActive,
    isStepEnabled,
    allStepsSaved,
    savedStepCount,
  };
}

export type UseWizardStateReturn = ReturnType<typeof useWizardState>;
